import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { CheckCircle, Sparkle, ArrowRight, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error | timeout
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let active = true;
    let n = 0;
    // Total budget: ~75 seconds (25 polls with mild backoff).
    // Stripe can take 20-45 s to finalize a checkout session (3DS / bank auth),
    // so the previous 20 s window timed out right before it would've worked.
    const MAX_POLLS = 25;
    const delayFor = (i) => {
      if (i < 5) return 1500;        // fast first few tries (card was fine)
      if (i < 12) return 3000;       // medium (3DS / SCA)
      return 5000;                    // slow tail (ACH-like delays)
    };
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          try {
            const me = await api.get("/auth/me");
            setUser(me.data);
          } catch {}
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (n < MAX_POLLS) {
          n += 1;
          setAttempts(n);
          setTimeout(poll, delayFor(n));
        } else {
          setStatus("timeout");
        }
      } catch {
        // Transient network errors — keep polling a few more times before giving up
        if (n < MAX_POLLS) {
          n += 1;
          setAttempts(n);
          setTimeout(poll, delayFor(n));
        } else {
          setStatus("error");
        }
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-20 fade-in text-center">
        {status === "polling" && (
          <>
            <div className="wave inline-flex mb-6"><span/><span/><span/><span/><span/></div>
            <h1 className="font-serif text-4xl mb-4">Confirming your payment…</h1>
            <p className="text-[color:var(--ink-muted)]">
              Stripe can take up to a minute to settle, especially with 3-D Secure.
              Sit tight — we&apos;ll flip the switch the moment it clears.
            </p>
            {attempts > 5 && (
              <p className="text-xs text-[color:var(--ink-faint)] mt-6 font-mono">
                Still verifying · attempt {attempts}/25
              </p>
            )}
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="text-[color:var(--success)] mx-auto mb-6"/>
            <div className="eyebrow mb-3 text-[color:var(--vermilion)]">Welcome to Plus</div>
            <h1 className="font-serif text-5xl mb-4">You&apos;re in. 🎉</h1>
            <p className="text-[color:var(--ink-muted)] max-w-lg mx-auto mb-10">
              Your subscription is live. Your caps just multiplied, Smart Review (auto-scheduling of the cards you keep missing) is unlocked when it ships,
              and premium voices are coming next. Thanks for supporting Ekkol.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Link to="/dashboard" className="inline-flex items-center gap-2 bg-[color:var(--ink)] text-white px-6 py-3 text-sm font-mono uppercase tracking-widest hover:bg-[color:var(--ink-2)]" data-testid="billing-success-dashboard">
                <Sparkle size={14} weight="fill"/> Go to my library
              </Link>
              <Link to="/profile" className="inline-flex items-center gap-2 border rule px-6 py-3 text-sm font-mono uppercase tracking-widest hover:bg-[color:var(--surface-alt)]">
                Profile &amp; settings <ArrowRight size={14}/>
              </Link>
            </div>
          </>
        )}

        {status === "expired" && (
          <>
            <Warning size={64} weight="fill" className="text-[color:var(--warning)] mx-auto mb-6"/>
            <h1 className="font-serif text-4xl mb-4">Session expired</h1>
            <p className="text-[color:var(--ink-muted)] mb-8">Try again from the Pricing page — nothing was charged.</p>
            <Link to="/pricing" className="inline-flex items-center gap-2 bg-[color:var(--ink)] text-white px-6 py-3 text-sm font-mono uppercase tracking-widest">
              Back to pricing <ArrowRight size={14}/>
            </Link>
          </>
        )}

        {(status === "error" || status === "timeout") && (
          <>
            <Warning size={64} weight="fill" className="text-[color:var(--warning)] mx-auto mb-6"/>
            <h1 className="font-serif text-4xl mb-4">Payment still confirming…</h1>
            <p className="text-[color:var(--ink-muted)] mb-4">
              Stripe charged you successfully — we just haven&apos;t gotten the final confirmation on our end yet.
              <strong> This is normal and your plan will update within a minute or two.</strong>
            </p>
            <p className="text-[color:var(--ink-muted)] mb-8 text-sm">
              Click &quot;Check again&quot; below, or refresh in 60 seconds.
              Still not working? Email <a href="mailto:support@ekkol.app" className="link-underline">support@ekkol.app</a> with your Stripe receipt.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <button
                onClick={() => { setAttempts(0); setStatus("polling"); window.location.reload(); }}
                className="inline-flex items-center gap-2 bg-[color:var(--ink)] text-white px-6 py-3 text-sm font-mono uppercase tracking-widest hover:bg-[color:var(--ink-2)]"
                data-testid="btn-check-again"
              >
                <Sparkle size={14} weight="fill"/> Check again
              </button>
              <Link to="/profile" className="inline-flex items-center gap-2 border rule px-6 py-3 text-sm font-mono uppercase tracking-widest hover:bg-[color:var(--surface-alt)]">
                Back to my profile <ArrowRight size={14}/>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
